/** Body property: how far the on-screen keyboard covers the page's bottom; unset without one. */
export const KEYBOARD_INSET_PROPERTY = '--streamlain-keyboard-inset';

interface VisualViewportBox {
  readonly height: number;
  readonly offsetTop: number;
  readonly scale: number;
}

/**
 * The page's bottom hidden by the on-screen keyboard: the keyboard shortens the visual viewport while
 * the layout viewport keeps its height. Page zoom is locked on touch devices (`maximum-scale=1`); a
 * zoomed page (desktop pinch) shows less of the page without any keyboard, so it counts as none.
 */
export function keyboardInset(
  layoutHeight: number,
  visual: VisualViewportBox
): number {
  if (visual.scale > 1) return 0;
  return Math.max(
    0,
    Math.round(layoutHeight - visual.height - visual.offsetTop)
  );
}

/**
 * Publishes the keyboard inset on the body, so controls anchored to the view's bottom (canvas dock,
 * AI island) rise above the keyboard. Returns the stop.
 */
export function watchKeyboardInset(win: Window): () => void {
  const visual = win.visualViewport;
  if (!visual) return () => {};
  const style = win.document.body.style;
  let applied = 0;
  const publish = () => {
    const inset = keyboardInset(win.innerHeight, visual);
    if (inset === applied) return;
    applied = inset;
    if (inset > 0) {
      style.setProperty(KEYBOARD_INSET_PROPERTY, `${inset}px`);
    } else {
      style.removeProperty(KEYBOARD_INSET_PROPERTY);
    }
  };
  publish();
  visual.addEventListener('resize', publish);
  visual.addEventListener('scroll', publish);
  return () => {
    visual.removeEventListener('resize', publish);
    visual.removeEventListener('scroll', publish);
    style.removeProperty(KEYBOARD_INSET_PROPERTY);
  };
}
